import { Box, CssBaseline } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Route, BrowserRouter as Router, Routes } from 'react-router-dom';
import './App.css';
import AxiosConfig from './config/AxiosConfig';
import Administration from './features/components/admin/Administration.jsx';
import Categorizations from './features/components/categorizations/Categorizations.jsx';
import DashboardFI from './features/components/dashboard/DashboardFI/DashboardFI.jsx';
import DashboardOrder from './features/components/dashboard/DashboardOrder/DashboardOrder.jsx';
import DashboardSC from './features/components/dashboard/DashboardSC/DashboardSC.jsx';
import { CreateTemplate } from './features/components/dashboard/DashboardTemplate/CreateTemplate.jsx';
import { DashboardTemplate } from './features/components/dashboard/DashboardTemplate/DashboardTemplate.jsx';
import Header from './features/components/header/Header.jsx';
import Inbox from './features/components/inbox/Inbox.jsx';
import ConnectionExpired from './features/components/login/Connection';
import Login from './features/components/login/Login.jsx';
import Notifications from './features/components/notifications/Notifications.jsx';
import Project from './features/components/project/Project.jsx';
import ProjectTicketDetails from './features/components/project/ticket/ProjectTicketDetails.jsx';
import ProjectTickets from './features/components/project/ticket/ProjectTickets.jsx';
import { ShortcutSettings } from './features/components/shortcut/ShortcutSettings.jsx';
import StatsPage from './features/components/stats/StatsPage.jsx';
import ErrorBoundary from './features/components/utils/errors/ErrorBoundary';
import InternalServerError from './features/components/utils/errors/InternalServerError.jsx';
import NotFound from './features/components/utils/errors/NotFound.jsx';
import PrivateRoutes from './features/components/utils/routes/PrivateRoutes.jsx';
import darkTheme from './features/helpers/themes/DarkTheme';
import lightTheme from './features/helpers/themes/LightTheme';
import {
  jssipuaInit,
  jssipuaStop,
} from './features/helpers/utils/jsSipUserAgent';
import UserMentionManager from './features/helpers/utils/UserMentionManager';
import useSystemStatus from './features/hooks/system/useSystemStatus';
import CheckVersion from './features/hooks/utils/checkVersion.jsx';
import useActions from './features/hooks/utils/useActions';
import useBrand from './features/hooks/utils/useBrand';
import useContactType from './features/hooks/utils/useContactType.js';
import useCurrency from './features/hooks/utils/useCurrency.js';
import useNoteTypes from './features/hooks/utils/useNoteTypes';
import useOrderCancelReasons from './features/hooks/utils/useOrderCancelReasons';
import useOrderRetractionReasons from './features/hooks/utils/useOrderRetractionReasons';
import useOrderStatus from './features/hooks/utils/useOrderStatus';
import useOrderTypes from './features/hooks/utils/useOrderTypes';
import usePaymentStatus from './features/hooks/utils/usePaymentStatus';
import usePermissions from './features/hooks/utils/usePermissions';
import useRefundReasons from './features/hooks/utils/useRefundReasons';
import useRefundStatus from './features/hooks/utils/useRefundStatus';
import useRefundTypes from './features/hooks/utils/useRefundTypes';
import useRoles from './features/hooks/utils/useRoles';
import useSavOrderReasons from './features/hooks/utils/useSavOrderReasons';
import useServices from './features/hooks/utils/useServices';
import useStores from './features/hooks/utils/useStores';
import useTicketTypes from './features/hooks/utils/useTicketTypes';
import useUsers from './features/hooks/utils/useUsers';
import {
  setActions,
  setBrand,
  setContactTypes,
  setCurrency,
  setNoteTypes,
  setOrderCancelReasons,
  setOrderRetractionReasons,
  setOrderStatus,
  setOrderTypes,
  setPaymentStatus,
  setPermissions,
  setRefundReasons,
  setRefundStatus,
  setRefundTypes,
  setRoles,
  setSavOrderReasons,
  setServices,
  setSnackBar,
  setStores,
  setSystem,
  setTicketTypes,
  setUsers,
} from './features/states/app/staticSlice';

const AppRoutes = () => {
  return (
    <>
      <Routes>
        <Route element={<PrivateRoutes />}>
          <Route element={<StatsPage />} path="/stats" />
          <Route element={<Inbox />} path="/inbox" />
          <Route element={<DashboardSC />} path="/dashboard-sc" />
          <Route element={<DashboardFI />} path="/dashboard-fi" />
          <Route element={<DashboardOrder />} path="/dashboard-order" />
          <Route element={<DashboardTemplate />} path="/dashboard-template" />
          <Route
            element={<CreateTemplate />}
            path="/dashboard-template/create"
          />
          <Route element={<Notifications />} path="/notifications" />
          <Route element={<ShortcutSettings />} path="/shortcuts" />
          <Route element={<Project />} path="/project/:projectId/">
            {/* PROJECT TICKETS */}
            <Route element={<Project />} path="tickets">
              {/* TICKET DETAILS */}
              <Route element={<ProjectTickets />} path=":ticketId">
                {/* TICKET ORDER */}
                <Route element={<ProjectTicketDetails />} path="order" />
                {/* TICKET ACTIVITIES */}
                <Route element={<ProjectTicketDetails />} path="activities" />
                {/* TICKET ACTIONS */}
                <Route element={<ProjectTicketDetails />} path="actions" />
                {/* TICKET RESPONSE */}
                <Route element={<ProjectTicketDetails />} path="response" />
                {/* TICKET TASKS */}
                <Route element={<ProjectTicketDetails />} path="tasks">
                  <Route element={<ProjectTicketDetails />} path="new" />
                  <Route element={<ProjectTicketDetails />} path=":taskId" />
                </Route>
              </Route>
            </Route>
            {/* PROJECT ACTIVITY */}
            <Route index element={<Project />} path="activity" />
            {/* PROJECT NOTES */}
            <Route index element={<Project />} path="notes" />
          </Route>
          <Route element={<Administration />} path="/admin" />
          <Route element={<Categorizations />} path="/categorizations" />
        </Route>
        <Route element={<Login />} path="/login" />
        <Route element={<InternalServerError />} path="/error" />
        <Route element={<NotFound />} path="/*" />
      </Routes>
    </>
  );
};

const App = () => {
  const dispatch = useDispatch();
  const profile = useSelector((state) => state?.login?.profile);
  const { users } = useUsers(profile?._id);
  const { ticketTypes } = useTicketTypes(profile?._id);
  const { actions } = useActions(profile?._id);
  const { services } = useServices(profile?._id);
  const { permissions } = usePermissions(profile?._id);
  const { refundStatus } = useRefundStatus(profile?._id);
  const { orderStatus } = useOrderStatus(profile?._id);
  const { orderTypes } = useOrderTypes(profile?._id);
  const { brand } = useBrand(profile?._id);
  const { currency } = useCurrency(profile?._id);
  const { contactType } = useContactType(profile?._id);
  const { paymentStatus } = usePaymentStatus(profile?._id);
  const { refundTypes } = useRefundTypes(profile?._id);
  const { refundReasons } = useRefundReasons(profile?._id);
  const { orderCancelReasons } = useOrderCancelReasons(profile?._id);
  const { orderRetractionReasons } = useOrderRetractionReasons(profile?._id);
  const { savOrderReasons } = useSavOrderReasons(profile?._id);
  const { system } = useSystemStatus();
  const { roles } = useRoles(profile?._id);
  const { noteTypes } = useNoteTypes(profile?._id);
  const { stores } = useStores(profile?._id);
  const themeColor = useSelector((state) => state?.header?.themeColor);
  const userMentionManager = new UserMentionManager(); // Créer l'instance de la classe
  const [snackBar, _setSnackBarProps] = useState({
    open: false,
    message: null,
    color: null,
  });

  // Mapping object for static data dispatches
  const STATIC_DATA_MAPPING = {
    users: { action: setUsers, data: 'users' },
    services: { action: setServices, data: 'services' },
    permissions: { action: setPermissions, data: 'permissions' },
    tickettypes: { action: setTicketTypes, data: 'ticketTypes' },
    actions: { action: setActions, data: 'actions' },
    refundstatuses: { action: setRefundStatus, data: 'refundStatus' },
    refundtypes: { action: setRefundTypes, data: 'refundTypes' },
    refundreasons: { action: setRefundReasons, data: 'refundReasons' },
    ordercancelreasons: {
      action: setOrderCancelReasons,
      data: 'orderCancelReasons',
    },
    orderstatus: { action: setOrderStatus, data: 'orderStatus' },
    ordertypes: { action: setOrderTypes, data: 'orderTypes' },
    brand: { action: setBrand, data: 'brand' },
    paymentstatus: { action: setPaymentStatus, data: 'paymentStatus' },
    roles: { action: setRoles, data: 'roles' },
    notetypes: { action: setNoteTypes, data: 'noteTypes' },
    stores: { action: setStores, data: 'stores' },
    orderretractionreasons: {
      action: setOrderRetractionReasons,
      data: 'orderRetractionReasons',
    },
    savorderreasons: { action: setSavOrderReasons, data: 'savOrderReasons' },
    system: { action: setSystem, data: 'system' },
    snackBar: { action: setSnackBar, data: 'snackBar' },
    contactType: { action: setContactTypes, data: 'contactType' },
    currency: { action: setCurrency, data: 'currency' },
  };

  const staticDatas = {
    users,
    services,
    permissions,
    ticketTypes,
    actions,
    refundStatus,
    refundTypes,
    refundReasons,
    orderCancelReasons,
    orderStatus,
    orderTypes,
    brand,
    currency,
    contactType,
    paymentStatus,
    roles,
    noteTypes,
    stores,
    orderRetractionReasons,
    savOrderReasons,
    system,
    snackBar,
  };

  // load datas when an user is logged in
  const handleStaticDatas = (data, setter) => {
    if (!profile) {
      return;
    }

    try {
      const mapping = STATIC_DATA_MAPPING[data];

      if (mapping) {
        // Use the mapping object to dispatch the corresponding action
        const stateData = staticDatas[mapping.data]; // Access the corresponding state data
        dispatch(mapping.action(stateData));
      } else if (setter) {
        // Handle custom data fetching
        AxiosConfig.create('get', `/${data}/`)
          .call()
          .exit((res) => {
            dispatch(setter(res));
          })
          .catch((error) => {
            console.error(`Error fetching ${data}:`, error);
            // Here you could also dispatch an error action or show a notification
          });
      }
    } catch (error) {
      console.error(`Error handling static data for ${data}:`, error);
      // Here you could also dispatch an error action or show a notification
    }
  };

  // load services into static
  useEffect(() => {
    handleStaticDatas('services');
  }, [services, profile]);

  // load ticket types into static
  useEffect(() => {
    handleStaticDatas('tickettypes');
  }, [ticketTypes, profile]);

  // load notes types into static
  useEffect(() => {
    handleStaticDatas('notetypes');
  }, [noteTypes, profile]);

  // load actions into static
  useEffect(() => {
    handleStaticDatas('actions');
  }, [actions, profile]);

  // load roles into static
  useEffect(() => {
    handleStaticDatas('roles');
  }, [roles, profile]);

  // load users into static
  useEffect(() => {
    handleStaticDatas('users');
    if (users && users.length > 0) {
      userMentionManager.initialize(users);
    }
  }, [users, profile]);

  useEffect(() => {
    handleStaticDatas('stores');
  }, [stores, profile]);

  useEffect(() => {
    handleStaticDatas('permissions', setPermissions);
  }, [permissions, profile]);

  useEffect(() => {
    handleStaticDatas('refundstatuses', setRefundStatus);
  }, [refundStatus, profile]);

  useEffect(() => {
    handleStaticDatas('refundtypes', setRefundTypes);
  }, [refundTypes, profile]);

  useEffect(() => {
    handleStaticDatas('refundreasons', setRefundReasons);
  }, [refundReasons, profile]);

  useEffect(() => {
    handleStaticDatas('ordercancelreasons', setOrderCancelReasons);
  }, [orderCancelReasons, profile]);

  useEffect(() => {
    handleStaticDatas('orderstatus', setOrderStatus);
  }, [orderStatus, profile]);

  useEffect(() => {
    handleStaticDatas('ordertypes', setOrderTypes);
  }, [orderTypes, profile]);

  useEffect(() => {
    handleStaticDatas('brand', setBrand);
  }, [brand, profile]);

  useEffect(() => {
    handleStaticDatas('currency', setCurrency);
  }, [currency, profile]);

  useEffect(() => {
    handleStaticDatas('paymentstatus', setPaymentStatus);
  }, [paymentStatus, profile]);

  useEffect(() => {
    handleStaticDatas('orderretractionreasons', setOrderRetractionReasons);
  }, [orderRetractionReasons, profile]);

  useEffect(() => {
    handleStaticDatas('savorderreasons', setSavOrderReasons);
  }, [savOrderReasons, profile]);

  useEffect(() => {
    handleStaticDatas('system', setSystem);
  }, [system, profile]);

  useEffect(() => {
    handleStaticDatas('snackBar', setSystem);
  }, [snackBar, profile]);

  useEffect(() => {
    handleStaticDatas('contactType', setContactTypes);
  }, [contactType, profile]);

  useEffect(() => {
    if (profile && import.meta.env.VITE_HOORTEL_ENABLED === 'true') {
      jssipuaInit(profile);
    }
    return () => {
      jssipuaStop();
    };
  }, [profile]);

  return (
    <ThemeProvider theme={themeColor === 'light' ? lightTheme : darkTheme}>
      <CssBaseline />
      <div className="App" style={{ width: '100vw' }}>
        <Router>
          {profile ? (
            <>
              <Header />
              <ConnectionExpired />
              <CheckVersion />
            </>
          ) : null}
          <Box sx={{ width: '100%', height: '100%' }}>
            <ErrorBoundary fallback={<InternalServerError />} source="App">
              <AppRoutes />
            </ErrorBoundary>
          </Box>
        </Router>
      </div>
    </ThemeProvider>
  );
};

export default App;
